// Alles wat de weekweergave 'rooster' (rijen links, dagen horizontaal) nodig
// heeft, op één plek. Twee schermen tonen hem: de agenda en de plankiezer bij
// het inplannen van een huisbezoek.
//
// Die twee zijn losse FullCalendar-instanties, en dat is precies het gevaar:
// de plankiezer liep al eerder uit de pas met de agenda (de kleuren van de
// blokken). Een tweede kopie van deze bedrading zou binnen weken afwijken —
// vandaar één hook die ze allebei aanroepen.
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook

import { useCallback, useMemo, useState } from 'react'
import resourcePlugin from '@fullcalendar/resource'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline'

import {
    TIMELINE_MODES,
    resourceIdsForTask,
    buildTimelineResources,
    knownResourceIds,
} from '../../helpers/calendar/timelineResources'
import { FULLCALENDAR_LICENSE_KEY, hasSchedulerLicense } from '../../helpers/calendar/schedulerLicense'

/** De naam van de weergave; ook de knop in de werkbalk heet zo. */
const TIMELINE_VIEW = 'resourceTimelineWeek'

/**
 * De weergave-instellingen. Een week met uurkolommen is 7 x 24 = 168 kolommen en
 * ruim 9500 pixels breed: dan scroll je een halve minuut voor je vrijdag ziet,
 * en juist het overzicht was de vraag. De werkdag in blokken van vier uur geeft
 * 21 kolommen — de hele week in één beeld.
 */
const TIMELINE_VIEW_CONFIG = {
    [TIMELINE_VIEW]: {
        type: 'resourceTimeline',
        duration: { weeks: 1 },
        buttonText: 'rooster',
        slotMinTime: '07:00:00',
        slotMaxTime: '19:00:00',
        slotDuration: { hours: 4 },
        slotLabelFormat: [
            { weekday: 'short', day: 'numeric', month: 'numeric' },
            { hour: '2-digit', minute: '2-digit', hour12: false },
        ],
    },
}

/**
 * @typedef {object} TimelineView
 * @property {boolean} enabled - Of de weergave aangeboden mag worden
 * @property {'personas'|'adresses'} mode - Wat er links onder elkaar staat
 * @property {(mode: 'personas'|'adresses') => void} setMode - Omschakelen
 * @property {boolean} showModeSwitch - Of de schakelaar getoond hoort te worden
 * @property {Array<object>} plugins - Bij de plugins van FullCalendar op te tellen
 * @property {object} options - Los op FullCalendar te spreiden ({...})
 * @property {object|undefined} views - Voor de prop `views`
 * @property {string} toolbarButton - Achter de knoppenlijst te plakken (leeg als uit)
 * @property {(task: object) => Array<string>} resourceIdsFor - De rij(en) van een taak
 * @property {(resourceId: string) => boolean} isPersonaRow - Of een rij een echte medewerker is
 */

/**
 * @param {object} input
 * @param {Array<{ID: string, Name?: string}>|null} [input.personas] - Actieve medewerkers; null laat de weergave uit
 * @param {Array<object>} [input.tasks] - De taken die in beeld kunnen komen
 * @param {boolean} [input.allowModeSwitch] - Of er tussen medewerkers en huishoudens gewisseld mag worden
 * @returns {TimelineView}
 */
const useTimelineView = ({ personas = null, tasks = [], allowModeSwitch = true }) => {
    const [mode, setMode] = useState(TIMELINE_MODES.personas)

    // Geen sleutel betekent geen knop: zonder geldige licentie tekent
    // FullCalendar een waarschuwing over de weergave heen, en die hoort geen
    // klant te zien. De aanroeper moet het bovendien zelf aanzetten door
    // `personas` mee te geven.
    const enabled = Array.isArray(personas) && hasSchedulerLicense()

    const resources = useMemo(
        () => (enabled ? buildTimelineResources({ mode, personas, tasks }) : []),
        [enabled, mode, personas, tasks]
    )

    const ids = useMemo(() => knownResourceIds(resources), [resources])

    const resourceIdsFor = useCallback(
        (task) => resourceIdsForTask(task, mode, ids),
        [mode, ids]
    )

    // Een rij is pas een medewerker als hij een echte persona is: de
    // verzamelrijen ('Niet toegewezen', 'Niet meer actief') zijn dat niet, en
    // wie op zo'n rij inplant hoort niemand toegewezen te krijgen.
    const isPersonaRow = useCallback(
        (resourceId) => mode === TIMELINE_MODES.personas
            && (personas || []).some((p) => p?.ID === resourceId),
        [mode, personas]
    )

    const options = useMemo(
        () => (enabled
            ? {
                schedulerLicenseKey: FULLCALENDAR_LICENSE_KEY,
                resources,
                // Zonder dit sorteert FullCalendar op `id`; de verzamelrijen
                // beginnen met een liggend streepje en belanden dan bovenaan.
                resourceOrder: 'volgorde',
                resourceAreaHeaderContent: mode === TIMELINE_MODES.adresses ? 'Huishouden' : 'Medewerker',
                resourceAreaWidth: '220px',
            }
            : {}),
        [enabled, resources, mode]
    )

    return {
        enabled,
        mode,
        setMode,
        showModeSwitch: enabled && allowModeSwitch,
        plugins: enabled ? [resourcePlugin, resourceTimelinePlugin] : [],
        options,
        views: enabled ? TIMELINE_VIEW_CONFIG : undefined,
        toolbarButton: enabled ? `,${TIMELINE_VIEW}` : '',
        resourceIdsFor,
        isPersonaRow,
    }
}

export { TIMELINE_VIEW, TIMELINE_VIEW_CONFIG }
export default useTimelineView
